import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Typography } from '@interstate/components/Typography';
import { TextInput } from '@interstate/components/TextInput';
import { Button } from '@interstate/components/Button';
import { EyeIcon } from '@interstate/components/Icons';
import type { StateTree } from '@makemydeal/dr-dash-types';
import * as customerSpdActionCreators from '@makemydeal/dr-dash-store/actions/CustomerSpdActionCreators';
import * as customerSpdSelectors from '@makemydeal/dr-dash-store/selectors/customerSpdSelectors';
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';
import { dealLockHooks } from '../hooks';
import { Dropdown, MaskTextInput, MaskDatePicker } from '@makemydeal/dr-shared-components';
import { NameFieldsWrapper, LoadingIndicatorWrapper } from './CustomerCard.styles';
import { CustomerSpdHeaderWrapper } from './CustomerSpd.styles';
import { StateCodes, getStateCodesSelectInputValues } from '@makemydeal/dr-shared-ui-utils';
import { SSN_PATTERN, DRIVERS_LICENCE_MASK, SSN_MASK } from './constants';
import { Spinner } from '@interstate/components/Spinner';
import { Alert } from '@interstate/components/Alert';
import type { DatePickerEventValue } from '@interstate/components/DatePicker';
import { DatePicker } from '@interstate/components/DatePicker';
import type { InterstateOnChangeEvent } from '@interstate/components/InterstateEvents';
import type { FormConfigValidationsErrors } from '@makemydeal/dr-dash-ui-utils';

interface CustomerSpdProps {
    commonConsumerId: string;
    formValues: any;
    formErrors: FormConfigValidationsErrors;
    isBusinessConversionFlow?: boolean;
    handleInputChange: (field: string, value: string) => void;
    handleDatePickerChange: (field: string, event: InterstateOnChangeEvent<DatePickerEventValue>) => void;
    handleDatePickerBlur: (field: string, event: InterstateOnChangeEvent<DatePickerEventValue>) => void;
}

const CustomerSpd: React.FC<CustomerSpdProps> = ({
    commonConsumerId,
    formValues,
    formErrors,
    isBusinessConversionFlow = false,
    handleInputChange,
    handleDatePickerChange,
    handleDatePickerBlur
}) => {
    const dispatch = useDispatch();
    const [showValues, setShowValues] = useState(false);
    const stateSelectInputValues = useMemo(getStateCodesSelectInputValues, StateCodes);
    const checkSpdDataByConsumerId = useSelector(customerSpdSelectors.getCheckCustomerSpdResult(commonConsumerId));
    const checkSpdData = !isBusinessConversionFlow ? checkSpdDataByConsumerId : undefined;
    const isCustomerLoading = useSelector(customerSpdSelectors.getCustomerSpdLoading());
    const customerError = useSelector(customerSpdSelectors.getCustomerSpdError(commonConsumerId));
    const isSpdDataAvailable = useMemo(() => {
        if (!checkSpdData) return false;
        const { driversLicenseState: _driversLicenseState, bankAccountNumber: _bankAccountNumber, ...otherFields } = checkSpdData;
        return Object.values(otherFields).some(Boolean);
    }, [checkSpdData]);
    const getCustomerDataSuccess = useSelector((state: StateTree) =>
        customerSpdSelectors.getCustomerAPISuccess(state, commonConsumerId)
    );
    const canDesk = useSelector((state: StateTree) => customerSpdSelectors.getHasDeskingPermission(state));
    const isLocked = dealLockHooks.useIsDealLocked();
    const isCreditAppFieldsCoverageEnabled = useSelector(featureToggleSelectors.isCreditAppFieldsCoverageEnabled);

    useEffect(() => {
        if (showValues && !getCustomerDataSuccess) {
            dispatch(customerSpdActionCreators.customerSpdRSAA(commonConsumerId));
            dispatch(customerSpdActionCreators.clickedShowCustomerSpd());
        }
    }, [showValues]);

    const onClickToggleData = () => {
        setShowValues(!showValues);
    };

    useEffect(() => {
        if (customerError) {
            setShowValues(false);
        }
    }, [customerError]);

    const driversLicenseExpirationDateProps = useMemo(
        () => ({
            size: 'small' as const,
            name: 'driversLicenseExpirationDate',
            label: 'DL Expiration Date',
            hasError: formErrors.driversLicenseExpirationDate?.hasError as boolean,
            errorMessage: formErrors.driversLicenseExpirationDate?.errorMessage as string,
            value: formValues.driversLicenseExpirationDate && new Date(formValues.driversLicenseExpirationDate),
            'data-testid': 'customer-form-drivers-license-expiration-date'
        }),
        [formValues.driversLicenseExpirationDate, formErrors.driversLicenseExpirationDate?.errorMessage]
    );

    const dateOfBirthProps = useMemo(
        () => ({
            size: 'small' as const,
            name: 'dateOfBirth',
            label: 'Date of Birth',
            helpMessage: 'Age must be at least 18 years',
            hasError: formErrors.dateOfBirth?.hasError as boolean,
            errorMessage: formErrors.dateOfBirth?.errorMessage as string,
            value: formValues.dateOfBirth && new Date(formValues.dateOfBirth),
            'data-testid': 'customer-form-date-of-birth'
        }),
        [formValues.dateOfBirth, formErrors.dateOfBirth?.errorMessage]
    );

    return (
        <>
            <CustomerSpdHeaderWrapper>
                <Typography variant="h5" sx={{ marginBottom: '1rem', marginTop: '1.5rem' }} data-testid="customer-spd-header">
                    Additional Information
                </Typography>

                {isSpdDataAvailable && canDesk && !isCustomerLoading && (
                    <Button
                        buttonStyle="tertiary"
                        size="small"
                        data-testid={`customer-card-${showValues ? 'hide' : 'show'}-data`}
                        endIcon={<EyeIcon />}
                        onClick={onClickToggleData}
                        disabled={isLocked}
                    >
                        {showValues ? 'Hide Data' : 'Show Data'}
                    </Button>
                )}
            </CustomerSpdHeaderWrapper>

            {isCustomerLoading ? (
                <LoadingIndicatorWrapper isFullHeight={false}>
                    <Spinner />
                </LoadingIndicatorWrapper>
            ) : (
                <>
                    {customerError && (
                        <Alert type="error" sx={{ marginBottom: '2rem' }} data-testid="customer-card-get-data-error">
                            <Typography variant="body-sm" tag="span">
                                Something went wrong while processing your request. Please try again.
                            </Typography>
                        </Alert>
                    )}
                    <NameFieldsWrapper columns={isCreditAppFieldsCoverageEnabled ? 4 : 3}>
                        {showValues ? (
                            <>
                                <TextInput
                                    name="driversLicenseNumber"
                                    label="Driver License Number"
                                    size="small"
                                    data-testid="customer-form-driver-license-number"
                                    onChange={(e) => handleInputChange('driversLicenseNumber', e.target.value)}
                                    value={formValues.driversLicenseNumber}
                                    sx={{ gridColumn: { xs: '1 / -1', sm: 'auto' } }}
                                    disabled={isLocked}
                                    hasError={formErrors.driversLicenseNumber?.hasError as boolean}
                                    errorMessage={formErrors.driversLicenseNumber?.errorMessage as string}
                                />

                                <Dropdown
                                    name="driversLicenseState"
                                    size="small"
                                    label="DL State"
                                    data-testid="customer-form-driver-license-state"
                                    options={stateSelectInputValues}
                                    onChange={(e) => handleInputChange('driversLicenseState', e.target.value || '')}
                                    value={formValues.driversLicenseState}
                                    allowRemoval={true}
                                    disabled={isLocked}
                                    hasError={formErrors.driversLicenseState?.hasError as boolean}
                                    errorMessage={formErrors.driversLicenseState?.errorMessage as string}
                                />

                                <DatePicker
                                    {...driversLicenseExpirationDateProps}
                                    data-testid="customer-form-drivers-license-expiration-date"
                                    onChange={(e) => handleDatePickerChange('driversLicenseExpirationDate', e)}
                                    disabled={isLocked}
                                />

                                <DatePicker
                                    {...dateOfBirthProps}
                                    data-testid="customer-form-date-of-birth"
                                    onChange={(e) => handleDatePickerChange('dateOfBirth', e)}
                                    onBlur={(e) => handleDatePickerBlur('dateOfBirth', e)}
                                    disabled={isLocked}
                                />

                                <TextInput
                                    name="socialSecurityNumber"
                                    label="Social Security Number"
                                    size="small"
                                    type="text"
                                    data-testid="customer-form-social-security-number"
                                    masking={SSN_PATTERN}
                                    onChange={(e) => handleInputChange('socialSecurityNumber', e.target.value)}
                                    value={formValues.socialSecurityNumber}
                                    hasError={formErrors.socialSecurityNumber.hasError as boolean}
                                    errorMessage={formErrors.socialSecurityNumber.errorMessage as string}
                                    disabled={isLocked}
                                />
                            </>
                        ) : (
                            <>
                                <MaskTextInput
                                    name="driversLicenseNumber"
                                    label="Driver License Number"
                                    size="small"
                                    type="text"
                                    data-testid="customer-form-driver-license-number"
                                    maskedValue={DRIVERS_LICENCE_MASK}
                                    onChange={handleInputChange}
                                    defaultMask={!!checkSpdData?.driversLicenseNumber}
                                    value={formValues.driversLicenseNumber}
                                    sx={{ gridColumn: { xs: '1 / -1', sm: 'auto' } }}
                                    disabled={isLocked}
                                    hasError={formErrors.driversLicenseNumber?.hasError as boolean}
                                    errorMessage={formErrors.driversLicenseNumber?.errorMessage as string}
                                />
                                <Dropdown
                                    name="driversLicenseState"
                                    size="small"
                                    label="DL State"
                                    data-testid="customer-form-driver-license-state"
                                    options={stateSelectInputValues}
                                    onChange={(e) => handleInputChange('driversLicenseState', e.target.value || '')}
                                    value={formValues.driversLicenseState}
                                    allowRemoval={true}
                                    disabled={isLocked}
                                    hasError={formErrors.driversLicenseState?.hasError as boolean}
                                    errorMessage={formErrors.driversLicenseState?.errorMessage as string}
                                />

                                <MaskDatePicker
                                    {...driversLicenseExpirationDateProps}
                                    handleDatePickerOnChange={handleDatePickerChange}
                                    defaultMask={!!checkSpdData?.driversLicenseExpirationDate}
                                    disabled={isLocked}
                                />

                                <MaskDatePicker
                                    {...dateOfBirthProps}
                                    handleDatePickerOnChange={handleDatePickerChange}
                                    defaultMask={!!checkSpdData?.dateOfBirth}
                                    handleDatePickerBlur={handleDatePickerBlur}
                                    disabled={isLocked}
                                />

                                <MaskTextInput
                                    name="socialSecurityNumber"
                                    label="Social Security Number"
                                    size="small"
                                    type="text"
                                    data-testid="customer-form-social-security-number"
                                    maskedValue={SSN_MASK}
                                    onChange={handleInputChange}
                                    defaultMask={!!checkSpdData?.socialSecurityNumber}
                                    value={formValues.socialSecurityNumber}
                                    masking={SSN_PATTERN}
                                    hasError={formErrors.socialSecurityNumber.hasError as boolean}
                                    errorMessage={formErrors.socialSecurityNumber.errorMessage as string}
                                    disabled={isLocked}
                                />
                            </>
                        )}
                    </NameFieldsWrapper>
                </>
            )}
        </>
    );
};

export default CustomerSpd;
